import type BraintreeCheckoutComponent from "@components/payment/BraintreeCheckout";

// Shared loader for the Braintree Drop-in chunk (U2, R1 — plan 2026-07-07-004).
//
// One memoized import promise serves both the pre-mount preload (kicked off
// from checkoutPrefetch.ts, parallel with the pricing request) and the
// lazy() render path in checkout.tsx — so the ~129 KiB gz chunk downloads
// once, starting at entry instead of after pricing resolves.
//
// The memo resets on rejection so a later attempt (pricing recovered, user
// hit retry) re-imports. NOTE: React 18's lazy() caches a rejected factory
// result permanently, so retry ALSO requires recreating the lazy component —
// checkout.tsx holds it in state for exactly that reason. Resetting the memo
// here is necessary but not sufficient on its own.

export type BraintreeChunkModule = {
  default: typeof BraintreeCheckoutComponent;
};

const mark = (name: string): void => {
  if (typeof performance !== "undefined" && performance.mark) {
    performance.mark(name);
  }
};

// Retries the import on failure (500ms then 1000ms backoff) — covers
// transient network blips without surfacing the error UI. Only a hard
// failure (e.g. 404 on a deploy-rotated chunk hash) rejects through.
export const createChunkLoader = <T>(
  importFn: () => Promise<T>,
  {
    attempts = 2,
    initialDelayMs = 500,
    onStart,
    onLoad,
  }: {
    attempts?: number;
    initialDelayMs?: number;
    onStart?: () => void;
    onLoad?: () => void;
  } = {},
) => {
  let promise: Promise<T> | null = null;

  const attempt = (attemptsLeft: number, delayMs: number): Promise<T> =>
    importFn().catch((err) => {
      if (attemptsLeft <= 0) throw err;
      return new Promise<void>((resolve) => setTimeout(resolve, delayMs)).then(
        () => attempt(attemptsLeft - 1, delayMs * 2),
      );
    });

  const get = (): Promise<T> => {
    if (!promise) {
      onStart?.();
      const p = attempt(attempts, initialDelayMs);
      promise = p;
      p.then(() => onLoad?.()).catch(() => {
        // Reset so the next get() re-imports (retry after recovery) —
        // unless a newer attempt already replaced this one.
        if (promise === p) promise = null;
      });
    }
    return promise;
  };

  // Fire-and-forget preload: failures are swallowed here (no unhandled
  // rejection at page entry); the render path observes them through its
  // own get() call.
  const start = (): void => {
    void get().catch(() => undefined);
  };

  return { get, start };
};

const loader = createChunkLoader<BraintreeChunkModule>(
  () => import("@components/payment/BraintreeCheckout"),
  {
    onStart: () => mark("omg:braintree-chunk-start"),
    onLoad: () => mark("omg:braintree-chunk-loaded"),
  },
);

export const getBraintreeChunk = loader.get;
export const startBraintreeChunkPreload = loader.start;
